import { defineProperty as _defineProperty, applyDecoratedDescriptor as _applyDecoratedDescriptor, initializerDefineProperty as _initializerDefineProperty } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { makeAutoObservable, reaction, observable } from 'mobx';
import { debounce, createSearchFriendlyString } from '@utils/common';
import { BookModel } from './BookModel.js';
import { UserDataModel } from './UserDataModel.js';

var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0, _descriptor1, _descriptor10, _descriptor11, _descriptor12, _descriptor13, _descriptor14, _descriptor15;
const DIGIT_REGEX = /\d+(,|\.)?\d*/g;

/** Specifies the available bookshelf-types */
let BookShelfType = /*#__PURE__*/function (BookShelfType) {
  BookShelfType[BookShelfType["DEFAULT"] = 0] = "DEFAULT";
  BookShelfType[BookShelfType["LAESLOES"] = 1] = "LAESLOES";
  return BookShelfType;
}({});

/** Specification of available filters in the shelf */
let BookShelfFilters = /*#__PURE__*/function (BookShelfFilters) {
  BookShelfFilters[BookShelfFilters["AUTHOR"] = 1] = "AUTHOR";
  BookShelfFilters[BookShelfFilters["GRADE"] = 2] = "GRADE";
  BookShelfFilters[BookShelfFilters["LEVEL"] = 4] = "LEVEL";
  BookShelfFilters[BookShelfFilters["SUBJECT"] = 8] = "SUBJECT";
  BookShelfFilters[BookShelfFilters["LET"] = 16] = "LET";
  BookShelfFilters[BookShelfFilters["LIX"] = 32] = "LIX";
  return BookShelfFilters;
}({});

/**
 * Describes how filters are internally independent of one another when
 * applied.
 */

/**
 * The shelf contain the collection of books and resources that are available.
 * All filtering will also be performed through this model.
 */
let BookShelfModel = (_class = class BookShelfModel {
  /** Apply data and subscribe to filter changes when the model is created */
  constructor(data) {
    /**
     * Specifies whether or not items should be categorized in the display or
     * not.
     */
    _defineProperty(this, "categorizeItems", false);
    /** Contains the id of the shelf */
    _defineProperty(this, "id", "");
    /**
     * Use this setting to search for books or resources that match the
     * specified search query.
     */
    _initializerDefineProperty(this, "searchQuery", _descriptor, this);
    /**
     * Use this setting to filter for books that matches any of the specified
     * authors.
     */
    _initializerDefineProperty(this, "authorFilter", _descriptor2, this);
    /**
     * Use this setting to filter for books that matches any of the specified
     * grades.
     */
    _initializerDefineProperty(this, "gradeFilter", _descriptor3, this);
    /**
     * Use this setting to filter for books that matches any of the specified
     * levels.
     */
    _initializerDefineProperty(this, "levelFilter", _descriptor4, this);
    /**
     * Use this setting to filter for books that matches any of the specified
     * subjects.
     */
    _initializerDefineProperty(this, "subjectFilter", _descriptor5, this);
    /**
     * Specifies whether or not the only the user's favorites should be
     * displayed.
     */
    _initializerDefineProperty(this, "showFavorites", _descriptor6, this);
    /**
     * Use this setting to filter for books with a lix complexity above the
     * given value.
     */
    _initializerDefineProperty(this, "minLixComplexity", _descriptor7, this);
    /**
     * Use this setting to filter for books with a lix complexity below the
     * given value.
     */
    _initializerDefineProperty(this, "maxLixComplexity", _descriptor8, this);
    /**
     * Use this setting to filter for books with a let complexity above the
     * given value.
     */
    _initializerDefineProperty(this, "minLetComplexity", _descriptor9, this);
    /**
     * Use this setting to filter for books with a let complexity below the
     * given value.
     */
    _initializerDefineProperty(this, "maxLetComplexity", _descriptor0, this);
    /**
     * allows disabling of certain features inside the bookshelf for better
     * customization.
     */
    _defineProperty(this, "disableSearch", void 0);
    _defineProperty(this, "disableFilters", void 0);
    _defineProperty(this, "disableLetAndLixFilters", void 0);
    _defineProperty(this, "disableAlphabeticGroups", void 0);
    /** Contains the full list of books available in this shelf */
    _defineProperty(this, "books", []);
    /** Contains the full list of categories available in this shelf */
    _defineProperty(this, "categories", []);
    /** Contains a specification of filter's internal independencies */
    _defineProperty(this, "filterIndependencies", {});
    /** Contains data about the user's interaction with this bookshelf */
    _defineProperty(this, "userData", void 0);
    _defineProperty(this, "gradeFilterLabel", void 0);
    _defineProperty(this, "levelFilterLabel", void 0);
    _defineProperty(this, "subjectFilterLabel", void 0);
    /**
     * Internal specification of which filters to apply when performing
     * filtering.
     */
    _defineProperty(this, "appliedFilters", [BookShelfFilters.AUTHOR, BookShelfFilters.GRADE, BookShelfFilters.LEVEL, BookShelfFilters.SUBJECT]);
    /**
     * Internal cache of the currently applied search query.
     *
     * This value differs from the public [[searchQuery]] property, as it is
     * only applied after the search query has remained constant for 350ms to
     * prevent CPU overloading while the user is typing his query.
     */
    _initializerDefineProperty(this, "appliedSearchQuery", _descriptor1, this);
    /**
     * Contains the list of available author filters, including their match
     * count.
     */
    _initializerDefineProperty(this, "availableAuthorFilters", _descriptor10, this);
    /**
     * Contains the list of available grade filters, including their match
     * count.
     */
    _initializerDefineProperty(this, "availableGradeFilters", _descriptor11, this);
    /**
     * Contains the list of available level filters, including their match
     * count.
     */
    _initializerDefineProperty(this, "availableLevelFilters", _descriptor12, this);
    /**
     * Contains the list of available subject filters, including their match
     * count.
     */
    _initializerDefineProperty(this, "availableSubjectFilters", _descriptor13, this);
    /** Internal map used to reference books by their ids */
    _defineProperty(this, "bookMap", void 0);
    /** Internal map used to reference books by their isbns */
    _defineProperty(this, "bookIsbnMap", void 0);
    /** Internal map used to reference categories by their ids */
    _defineProperty(this, "categoryMap", void 0);
    /**
     * Contains the list of categories that match the current search criteria.
     */
    _initializerDefineProperty(this, "matchingCategories", _descriptor14, this);
    /** Contains the list of books that match the current search criteria */
    _initializerDefineProperty(this, "matchingBooks", _descriptor15, this);
    /** Will contain a reference to the current filter event listener */
    _defineProperty(this, "onFilterCallback", void 0);
    /** Helper that resets any applied filters */
    _defineProperty(this, "resetFilters", () => {
      this.searchQuery = this.appliedSearchQuery = "";
      this.minLetComplexity = 0;
      this.maxLetComplexity = this.highestLetComplexity;
      this.minLixComplexity = 0;
      this.maxLixComplexity = this.highestLixComplexity;
      this.authorFilter = [];
      this.gradeFilter = [];
      this.levelFilter = [];
      this.subjectFilter = [];
    });
    /**
     * Internal helper that applies search queries in a debounced manner in
     * order to prevent CPU-overloading if the search query changes frequently.
     */
    _defineProperty(this, "applySearchQuery", debounce(() => {
      this.appliedSearchQuery = createSearchFriendlyString(this.searchQuery);
    }, 350));
    Object.assign(this, data);

    // Set max lix and let complexity filters to the highest possible
    // values
    this.maxLetComplexity = this.highestLetComplexity;
    this.maxLixComplexity = this.highestLixComplexity;
    makeAutoObservable(this);

    // Subscribe to changes in the search query and make sure that it's
    // applied in a debounced manner
    reaction(() => this.searchQuery, () => {
      this.applySearchQuery();
    });

    // Subscribe to filter changes and ensure that matching books and
    // resources are kept up-to-date
    reaction(() => this.getSearchKey(), () => {
      this.performFiltering();
    });

    // Perform initial filtering right away
    this.performFiltering();
  }

  /** Helper that calculates the highest let complexity in the shelf */
  get highestLetComplexity() {
    let highestLetComplexity = 0;

    // Loop through all books and identify the highest available lix
    // complexity score
    for (const book of this.books) {
      // Skip books that doesn't have a lix complexity specified
      if (book.letComplexity === undefined) {
        continue;
      }
      highestLetComplexity = Math.max(highestLetComplexity, book.letComplexity);
    }

    // Round up to nearest 5 to prettify the user interface
    return Math.ceil(highestLetComplexity / 5) * 5;
  }

  /** Helper that calculates the highest lix complexity in the shelf */
  get highestLixComplexity() {
    let highestLixComplexity = 0;

    // Loop through all books and identify the highest available lix
    // complexity score
    for (const book of this.books) {
      // Skip books that doesn't have a lix complexity specified
      if (book.lixComplexity === undefined) {
        continue;
      }
      highestLixComplexity = Math.max(highestLixComplexity, book.lixComplexity);
    }

    // Round up to nearest 5 to prettify the user interface
    return Math.ceil(highestLixComplexity / 5) * 5;
  }

  /**
   * Use this method to fetch data for the specified user, and apply that data
   * internally to ensure that his favorites and progressions are being
   * rendered within the component.
   */
  async fetchUserData(userId, userInstitutionId, productId) {
    Object.assign(this, {
      userData: userId && userInstitutionId && productId ? await UserDataModel.FETCH(userId, userInstitutionId, productId, this) : undefined
    });
  }

  /** Helper that finds a book given by it's id */
  getCategoryById(id) {
    // If books haven't been mapped by their ids yet, then do it now
    if (!this.categoryMap) {
      const categoryMap = new Map();
      for (const category of this.categories) {
        categoryMap.set(category.id, category);
      }
      Object.assign(this, {
        categoryMap
      });
    }

    // Return reference to the book
    return this.categoryMap.get(id);
  }

  /** Helper that finds a book given by it's id */
  getBookById(id) {
    // If books haven't been mapped by their ids yet, then do it now
    if (!this.bookMap) {
      const bookMap = new Map();
      for (const book of this.books) {
        bookMap.set(book.id, book);
      }
      Object.assign(this, {
        bookMap
      });
    }

    // Return reference to the book
    return this.bookMap.get(id);
  }

  /** Helper that finds a book given by it's id */
  getBookByIsbn(isbn) {
    // If books haven't been mapped by their ids yet, then do it now
    if (!this.bookIsbnMap) {
      const bookIsbnMap = new Map();
      for (const book of this.books) {
        bookIsbnMap.set(book.isbn, book);
      }
      Object.assign(this, {
        bookIsbnMap
      });
    }

    // Return reference to the book
    return this.bookIsbnMap.get(isbn);
  }

  /** Helper that finds a book or a resource given by it's id */
  getItemById(id) {
    return this.getBookById(id);
  }

  /**
   * Helper that determines if there is currently any filtering applied on
   * the shelf.
   */
  isFiltering() {
    // Specify that we're searching if...
    return (
      // ... a search query is applied
      this.appliedSearchQuery !== "" ||
      // ... we're filtering by let complexity
      this.minLetComplexity > 0 || this.maxLetComplexity < this.highestLetComplexity ||
      // ... we're filtering by lix complexity
      this.minLixComplexity > 0 || this.maxLixComplexity < this.highestLixComplexity ||
      // ... we're filtering by authors
      this.authorFilter.length > 0 ||
      // ... we're filtering by grades
      this.gradeFilter.length > 0 ||
      // ... we're filtering by levels
      this.levelFilter.length > 0 ||
      // ... we're filtering by subjects
      this.subjectFilter.length > 0
    );
  }

  /**
   * Helper that generates a unique key based on the current search parameters
   * (this is useful, for instance, if we'd like react to crossfade every time
   * a new resultset is being rendered).
   */
  getSearchKey() {
    return [`q=${this.appliedSearchQuery}`, `minLet=${this.minLetComplexity}`, `maxLet=${this.maxLetComplexity}`, `minLix=${this.minLixComplexity}`, `maxLix=${this.maxLixComplexity}`, `authors=${this.authorFilter.join(",")}`, `grades=${this.gradeFilter.join(",")}`, `levels=${this.levelFilter.join(",")}`, `subjects=${this.subjectFilter.join(",")}`].join("&");
  }

  /**
   * Helper that determines if there are multiple categories available in the
   * library. If this is the case, then the available items should be rendered
   * separated into multiple groups based on their categories.
   */
  hasMultipleCategories() {
    return this.findMatchingCategories(new Set(this.books)).length > 1;
  }
  /** Helper that returns a list of all categories within the shelf */
  getCategories() {
    const result = this.categories.slice(0);

    // Determine if there are any uncategorized items anywhere
    let containsUncategorisedItems = false;
    for (const item of this.getItems()) {
      if (item.category === undefined) {
        containsUncategorisedItems = true;
        break;
      }
    }

    // If the shelf contains uncategorized items, then push an undefined
    // category to the list
    if (containsUncategorisedItems) {
      result.push(undefined);
    }
    return result;
  }

  /** Helper that finds all items under the specified category id */
  getItemsByCategoryId(categoryId) {
    const result = [];

    // Apply all matching books to the resultset
    for (const book of this.books) {
      if (book.category === categoryId) {
        result.push(book);
      }
    }

    // Sort the resultset alphabetically
    return result.sort((a, b) => {
      const a2 = a.title.replace(DIGIT_REGEX, this.padNumbersForNaturalSort);
      const b2 = b.title.replace(DIGIT_REGEX, this.padNumbersForNaturalSort);
      if (a2 === b2) {
        return 0;
      }
      return a2 > b2 ? 1 : -1;
    });
  }

  /** Helper that returns a list of all items in the shelf */
  getItems() {
    const result = this.books;
    return result.slice().sort((a, b) => {
      const a2 = a.title.replace(DIGIT_REGEX, this.padNumbersForNaturalSort);
      const b2 = b.title.replace(DIGIT_REGEX, this.padNumbersForNaturalSort);
      if (a2 === b2) {
        return 0;
      }
      return a2 > b2 ? 1 : -1;
    });
  }

  /**
   * Helper that returns all categories matching the specified search
   * criteria.
   */
  getMatchingCategories() {
    return this.matchingCategories;
  }

  /**
   * Helper that finds all matching items available under the specified
   * category id.
   */
  getMatchingItemsByCategoryId(categoryId) {
    const result = [];

    // Apply all matching books to the resultset
    for (const book of this.matchingBooks) {
      if (book.category === categoryId) {
        result.push(book);
      }
    }

    // Sort the resultset alphabetically
    return result.sort((a, b) => {
      const a2 = a.title.replace(DIGIT_REGEX, this.padNumbersForNaturalSort);
      const b2 = b.title.replace(DIGIT_REGEX, this.padNumbersForNaturalSort);
      if (a2 === b2) {
        return 0;
      }
      return a2 > b2 ? 1 : -1;
    });
  }

  /**
   * Helper that returns a list of all items in the shelf matching the current
   * search criteria.
   */
  getMatchingItems() {
    const result = this.matchingBooks;
    return result.slice().sort((a, b) => {
      const a2 = a.title.replace(DIGIT_REGEX, this.padNumbersForNaturalSort);
      const b2 = b.title.replace(DIGIT_REGEX, this.padNumbersForNaturalSort);
      if (a2 === b2) {
        return 0;
      }
      return a2 > b2 ? 1 : -1;
    });
  }

  /** Helper that returns author filters publicly */
  getAuthorFilters() {
    return this.availableAuthorFilters;
  }

  /** Helper that returns grade filters publicly */
  getGradeFilters() {
    return this.availableGradeFilters;
  }

  /** Helper that returns level filters publicly */
  getLevelFilters() {
    return this.availableLevelFilters;
  }

  /** Helper that returns subject filters publicly */
  getSubjectFilters() {
    return this.availableSubjectFilters;
  }

  /** Registers a listener for the filter event */
  onFilter(callback) {
    this.onFilterCallback = callback;
  }

  /**
   * Helper that loops through all applied filters and extracts matching items
   * as well as updates the list of available filters and the match count of
   * their values.
   */
  performFiltering() {
    // Start off by finding all books and resources that match the basic
    // applied filtering (such as search query and let / lix complexity)
    const bookSubset = this.findMatchingBooksBySearchAndComplexity(this.books);

    // Merge books and resources into a single list of items that we'll be
    // performing further filtering on
    const itemSubset = bookSubset;

    // Find matches by each of the applied filters one-by-one, so that we
    // can later concatenate these matches
    const matches = new Map();
    for (const filter of this.appliedFilters) {
      matches.set(filter, new Set(this.findMatchingItemsByFilter(itemSubset, filter)));
    }

    // Loop over all filter independencies, and fix them once and for all by
    // merging filter results with it's independencies
    for (const filter of this.appliedFilters) {
      if (this.filterIndependencies[filter]) {
        // Generate matches which this filter is independent of
        const independentMatches = this.filterIndependencies[filter]?.map(independentFilter => {
          // If this filter hasn't been applied, then don't
          // ignore it when calculating matching
          const filterProp = this.getFilterProp(independentFilter);
          if (!filterProp || !this[filterProp].length) {
            return [];
          }

          // ... Otherwise apply matched values
          return [...matches.get(independentFilter)];
        }).reduce((a, b) => a.concat(b), []) ?? [];
        matches.set(filter, new Set([...matches.get(filter), ...independentMatches]));
      }
    }

    // Concatenate matches by groups of dependencies, which will allow us to
    // extract number of matches for each of the available filters and
    // finally generate a full list of matching books
    for (const filter of this.appliedFilters) {
      // Create a dependencyGroup id, so that filters that share the same
      // dependencies will only be calculated once
      const dependencyGroupId = this.getFilterDependencyGroupId(filter);

      // Identify matches based on the dependency group
      if (!matches.has(dependencyGroupId)) {
        matches.set(dependencyGroupId, this.getFilterDependencyMatches(filter, matches));
      }
    }

    // Once we get here, we can start calculating the number of matches for
    // each of the options available in the different filters
    for (const filter of this.appliedFilters) {
      this.identifyAvailableFilterValues(filter, matches);
    }

    // Identify matches from each of the available filters, by searching
    // within their dependency groups
    const filterMatches = [];
    for (const filter of this.appliedFilters) {
      filterMatches.push(matches.get(filter));
    }

    // With this set of values that match each of our available filters, we
    // can now finally create one single list of matches
    const matchingItems = this.concatenateMatchingItems(filterMatches);
    const matchingBooks = [];

    // Now we just need to divide the list into books and resources
    for (const matchingItem of matchingItems) {
      if (matchingItem instanceof BookModel) {
        matchingBooks.push(matchingItem);
      }
    }

    // Apply the matching items and books internally
    this.matchingBooks = matchingBooks;

    // Last thing to do is to identify matching categories
    this.matchingCategories = this.findMatchingCategories(matchingItems);

    // Trigger "onFilterCallback" if one was supplied, to allow custom hooks
    // into newly filtered results
    if (this.onFilterCallback) {
      this.onFilterCallback();
    }
  }

  /**
   * Helper that loops through a list of books and identifies which of them
   * that match the currently applied search query and complexity filtering.
   */
  findMatchingBooksBySearchAndComplexity(books) {
    const searchQuery = this.appliedSearchQuery;
    const minLetComplexity = this.minLetComplexity;
    const maxLetComplexity = this.maxLetComplexity;
    const minLixComplexity = this.minLixComplexity;
    const maxLixComplexity = this.maxLixComplexity;
    let matches = books.filter(book => {
      // Perform filtering based on let complexity
      if (book.letComplexity < minLetComplexity) {
        return false;
      } else if (book.letComplexity > maxLetComplexity) {
        return false;
      }

      // Perform filtering based on lix complexity
      if (book.lixComplexity < minLixComplexity) {
        return false;
      } else if (book.lixComplexity > maxLixComplexity) {
        return false;
      }

      // Perform filtering based on search query
      if (searchQuery && book.searchableContent.indexOf(searchQuery) === -1) {
        return false;
      }

      // If we get here, the book is a match
      return true;
    });

    // if we're filtering by let or lix, then remove all books that don't
    // have a "real" value (ie. one above 0) to eliminate false matches
    if (maxLetComplexity < this.highestLetComplexity) {
      matches = matches.filter(book => {
        return book.letComplexity > 0;
      });
    }
    if (maxLixComplexity < this.highestLixComplexity) {
      matches = matches.filter(book => {
        return book.lixComplexity > 0;
      });
    }
    return matches;
  }

  /**
   * Helper that translates a ShelfFilter into the name of the propery on
   * which applied values are available on this model.
   */
  getFilterProp(filter) {
    switch (filter) {
      case BookShelfFilters.AUTHOR:
        return "authorFilter";
      case BookShelfFilters.GRADE:
        return "gradeFilter";
      case BookShelfFilters.LEVEL:
        return "levelFilter";
      case BookShelfFilters.SUBJECT:
        return "subjectFilter";
      default:
        // No other supported filters, bail out
        return undefined;
    }
  }

  /**
   * Helper that translates a ShelfFilter into the name of the propery on
   * which available values are available on this model.
   */
  getAvailableFiltersProp(filter) {
    switch (filter) {
      case BookShelfFilters.AUTHOR:
        return "availableAuthorFilters";
      case BookShelfFilters.GRADE:
        return "availableGradeFilters";
      case BookShelfFilters.LEVEL:
        return "availableLevelFilters";
      case BookShelfFilters.SUBJECT:
        return "availableSubjectFilters";
      default:
        // No other supported filters, bail out
        return undefined;
    }
  }

  /**
   * Helper that translates a ShelfFilter into the name of the propery on
   * which an items values are available on Book- and ResourceModels.
   */
  getValueProp(filter) {
    switch (filter) {
      case BookShelfFilters.AUTHOR:
        return "authors";
      case BookShelfFilters.GRADE:
        return "grades";
      case BookShelfFilters.LEVEL:
        return "levels";
      case BookShelfFilters.SUBJECT:
        return "subjects";
      default:
        // No other supported filters, bail out
        return undefined;
    }
  }

  /**
   * Helper that loops through a list of items and identifies which of them
   * that match the specified filter.
   */
  findMatchingItemsByFilter(items, filter) {
    const filterProp = this.getFilterProp(filter);
    const valueProp = this.getValueProp(filter);

    // Bail out by simply returning the entire set of input items, if either
    // filter or value prop couldn't be extracted
    if (!filterProp || !valueProp) {
      return items;
    }
    const filterValues = arrayToObject(this[filterProp]);

    // If no filtering has been applied, then return the entire set of items
    if (!filterValues) {
      return items;
    }

    // ... Otherwise perform filtering
    return items.filter(item => isValuesInObject(item[valueProp], filterValues));
  }

  /** Helper that determines which filters the given filter is dependent on */
  getFilterDependencies(filter) {
    const independencies = this.filterIndependencies[filter] || [];
    const dependencies = [];
    if (filter !== BookShelfFilters.AUTHOR && independencies.indexOf(BookShelfFilters.AUTHOR) === -1) {
      dependencies.push(BookShelfFilters.AUTHOR);
    }
    if (filter !== BookShelfFilters.GRADE && independencies.indexOf(BookShelfFilters.GRADE) === -1) {
      dependencies.push(BookShelfFilters.GRADE);
    }
    if (filter !== BookShelfFilters.LEVEL && independencies.indexOf(BookShelfFilters.LEVEL) === -1) {
      dependencies.push(BookShelfFilters.LEVEL);
    }
    if (filter !== BookShelfFilters.SUBJECT && independencies.indexOf(BookShelfFilters.SUBJECT) === -1) {
      dependencies.push(BookShelfFilters.SUBJECT);
    }
    return dependencies;
  }

  /**
   * Helper that generates the a unique id for the group of filters that the
   * given input filter is dependent on. This makes it easy for us to ensure
   * that we don't perform any unneccessary filtering by concatenating
   * multiple filters.
   */
  getFilterDependencyGroupId(filter) {
    return this.getFilterDependencies(filter).reduce((a, b) => a + b, 0);
  }

  /**
   * Helper that generates a concatenated set of items that matches the given
   * filters dependencies. This way we can easily extrapolate how many matches
   * each available item has.
   */
  getFilterDependencyMatches(filter, matches) {
    const dependencies = this.getFilterDependencies(filter);
    return this.concatenateMatchingItems(dependencies.map(dependencyMatches => {
      return matches.get(dependencyMatches);
    }));
  }

  /**
   * Helper that concatenates multiple sets of items, returning only items
   * that are present in all input sets.
   */
  concatenateMatchingItems(matchSets) {
    const matchCount = new Map();
    const output = new Set();

    // Loop through matching items in each of the given matchSets, and count
    // the number of times each item has been matched
    for (const matchSet of matchSets) {
      for (const item of matchSet) {
        // Initialize items in the match counter
        if (!matchCount.has(item)) {
          matchCount.set(item, 0);
        }

        // Increment match count
        matchCount.set(item, matchCount.get(item) + 1);
      }
    }

    // Add all items that are available in each of the given match sets to
    // final output
    const matchSetCount = matchSets.length;
    for (const [item, matches] of matchCount) {
      if (matches === matchSetCount) {
        output.add(item);
      }
    }
    return output;
  }

  /**
   * Helper that identifies the available values for the given filter as well
   * as the number of matching items within the subset of dependent filter
   * matches.
   */
  identifyAvailableFilterValues(filter, matches) {
    // Start by getting a complete list of values for this filter
    const filterProp = this.getAvailableFiltersProp(filter);
    const valueProp = this.getValueProp(filter);

    // Bail out if the value prop couldn't be found
    if (!valueProp || !filterProp) {
      return;
    }
    const values = this.identifyAllFilterValues(valueProp);

    // Extract items matched by the this filters dependencies
    const dependencyGroupId = this.getFilterDependencyGroupId(filter);
    const dependentMatches = Array.from(matches.get(dependencyGroupId));

    // Loop through all items matching dependencies for this filter, and
    // generate a map of values present on each of them (this will greatly
    // optimise performance when we count number of matching items within
    // the subset of dependent matches for each value)
    const itemValues = dependentMatches.map(item => arrayToObject(item[valueProp]));

    // Finally loop through all values, and count the number of matches for
    // each of them in the subset of items matched by dependent filters
    const result = {};
    values.forEach(value => {
      result[value] = itemValues.filter(item => item !== null && item[value] === true).length;
    });

    // Make sure that results are made internally available
    this[filterProp] = result;
  }

  /**
   * Helper that identifies all values on the given prop across all items in
   * the shelf.
   */
  identifyAllFilterValues(valueProp) {
    const values = new Set();

    // Generate a combined list of books and resources, so that we can
    // extract the required values from each and all of them
    const items = this.books;

    // Loop through all items and extract the desired values
    for (const item of items) {
      if (item[valueProp] === undefined) {
        continue;
      }

      // Type guard doesn't work because we're using variable references,
      // but efter having just checked for === undefined up above, we can
      // safely assume that values will be an array of strings at this
      // point
      const itemValues = item[valueProp];
      for (const value of itemValues) {
        values.add(value);
      }
    }

    // Helper that pads digits
    return [...values].sort((a, b) => {
      const a2 = a.replace(DIGIT_REGEX, this.padNumbersForNaturalSort);
      const b2 = b.replace(DIGIT_REGEX, this.padNumbersForNaturalSort);
      return a2 > b2 ? 1 : -1;
    });
  }

  /**
   * Helper that ensures that numbers are always padded up to at least 3
   * digits.
   */
  padNumbersForNaturalSort(digits) {
    return digits.padStart(10, "0");
  }

  /** Returns the full list of unique categories available in the shelf */
  findMatchingCategories(items) {
    // Loop through all matching books and resources and generate a list of
    // category IDs that actually contain books
    const matchingCategoryIds = new Set();
    let containsUncategorisedItems = false;
    for (const item of items) {
      if (item.category !== undefined) {
        matchingCategoryIds.add(item.category);
      } else {
        containsUncategorisedItems = true;
      }
    }

    // Loop over all matching categories, and prepare the output
    const matchingCategories = [];
    for (const category of this.categories) {
      // Skip categories that wasn't matched
      if (!matchingCategoryIds.has(category.id)) {
        continue;
      }

      // ... Otherwise push the category
      matchingCategories.push(category);
    }

    // If the shelf contains uncategorised items, then push an undefined
    // entry to the result
    if (containsUncategorisedItems) {
      matchingCategories.push(undefined);
    }

    // Return result
    return matchingCategories;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "searchQuery", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return "";
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "authorFilter", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "gradeFilter", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "levelFilter", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "subjectFilter", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "showFavorites", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "minLixComplexity", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "maxLixComplexity", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return Infinity;
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "minLetComplexity", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "maxLetComplexity", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return Infinity;
  }
}), _descriptor1 = _applyDecoratedDescriptor(_class.prototype, "appliedSearchQuery", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return "";
  }
}), _descriptor10 = _applyDecoratedDescriptor(_class.prototype, "availableAuthorFilters", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor11 = _applyDecoratedDescriptor(_class.prototype, "availableGradeFilters", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor12 = _applyDecoratedDescriptor(_class.prototype, "availableLevelFilters", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor13 = _applyDecoratedDescriptor(_class.prototype, "availableSubjectFilters", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor14 = _applyDecoratedDescriptor(_class.prototype, "matchingCategories", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor15 = _applyDecoratedDescriptor(_class.prototype, "matchingBooks", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _class);

/**
 * Helper that converts an array into an object with keys based on the values,
 * as these will be much faster to look up while performing filtering.
 */
function arrayToObject(values) {
  // Bail out if there are no values in the array
  if (values === undefined || !values.length) {
    return null;
  }
  const result = {};

  // Convert values to object keys
  for (const value of values) {
    result[value] = true;
  }
  return result;
}

/**
 * Helper that determines if at least one of the given values are present as
 * keys in the object. This is a fast way to perform filtering, based on the
 * objects created by [[arrayToObject]] method.
 */
function isValuesInObject(values, object) {
  // Bail out if values are not given
  if (!values) {
    return false;
  }

  // Convert input to an array
  const input = Array.isArray(values) ? values : [values];

  // Determine if at least one of the given values are present
  for (const value of input) {
    if (object[value] === true) {
      return true;
    }
  }

  // If we get here, neither of the values are present in the object
  return false;
}

export { BookShelfFilters, BookShelfModel, BookShelfType };
//# sourceMappingURL=BookShelfModel.js.map
